'use client'

import { useCallback, useEffect } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'

import type { CosmeticProfileResponse } from '@/features/profiles/types/avatar-catalog'
import { api } from '@/shared/services/api'

const queryKey = ['user', 'cosmetics', 'profile'] as const

export function useAvatarCatalog(enabled: boolean) {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const { data } = await api.get<CosmeticProfileResponse>('/user/cosmetics/profile')
      return data
    },
    enabled
  })

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey })
  }, [queryClient])

  useEffect(() => {
    if (!enabled) return

    const onVisible = () => {
      void queryClient.invalidateQueries({ queryKey })
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') onVisible()
    }

    window.addEventListener('focus', onVisible)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('focus', onVisible)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [enabled, queryClient])

  return {
    ...query,
    refetchCatalog: query.refetch,
    invalidateCatalog: invalidate
  }
}
